import React from 'react'
import Modal from '../ui/Modal'
import {
  Building2,
  MapPin,
  Award,
  LayoutTemplate,
  Waves,
  Dumbbell,
  Baby,
  Shield,
  Cpu,
  Leaf,
  Users,
  PartyPopper,
  Wrench,
  GraduationCap,
  Briefcase,
  HeartPulse,
  Bus,
  Clapperboard,
  CheckCircle2,
  XCircle,
  MinusCircle,
  BadgeCheck,
  Sparkles,
} from 'lucide-react'

import "../../styles/PropertyDetails.css"
import PropertyDetailsAccordian from './PropertyDetailsAccordian'

/* ─── helpers ─── */

const toTitleCase = (str) =>
  str
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())

    const CATEGORY_ICONS = {
      fitness: HeartPulse,
      swimming: Waves,
      gym: Dumbbell,
      sports: Award,
      entertainment: PartyPopper,
      kids: Baby,
      community: Users,
      green_space: Leaf,
      security: Shield,
      technology: Cpu,
      default: Wrench,
    };

    function iconFor(categories = [], name) {
      const lowerName = name.toLowerCase();

      if(lowerName === "gym") {
        return CATEGORY_ICONS.gym;
      }
      if(lowerName.includes("pool") || lowerName.includes("swimming")) {
        return CATEGORY_ICONS.swimming;
      }
      for (const cat of categories) {
        if (CATEGORY_ICONS[cat]) {
          return CATEGORY_ICONS[cat];
        }
      }
      return CATEGORY_ICONS.default;
    }

    function AmenityIcon({ categories = [], name }) {
      const Icon = iconFor(categories, name);

      return (
        <Icon
          size={13}
          style={{ color: 'var(--color-light-blue)' }}
        />
      );
    }
      const LANDMARK_ICONS = {
        schools: GraduationCap,
        it_parks: Briefcase,
        hospitals: HeartPulse,
        transport: Bus,
        entertainment: Clapperboard,
      }

function LandmarkIcon({ category }) {
  const Icon = LANDMARK_ICONS[category] || MapPin
  return <Icon size={13} style={{ color: 'var(--color-light-blue)' }} />
}

function ApprovalIcon({ value }) {
  if (value === true)
    return <CheckCircle2 size={18} style={{ color: 'var(--color-hot)' }} />
  if (value === false)
    return <XCircle size={18} style={{ color: 'var(--color-medium-gray)' }} />
  return <MinusCircle size={18} style={{ color: 'var(--color-medium-gray)' }} />
}

function ApprovalText({ value }) {
  if (value === true) return 'Yes'
  if (value === false) return 'No'
  return 'Not specified'
}

/* ─── shared primitives ─── */

const styles = {
  sectionTitle: {
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--color-medium-gray)',
    textTransform: 'uppercase',
    letterSpacing: '0.07em',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginBottom: '0.75rem',
  },
  sectionIcon: {
    color: 'var(--color-light-blue)',
  },
  card: {
    background: 'var(--color-white)',
    border: '1px solid var(--color-border)',
    borderRadius: 10,
    padding: '0.75rem 1rem',
  },
  label: {
    fontSize: 11,
    color: 'var(--color-medium-gray)',
    fontWeight: 500,
    marginBottom: 4,
  },
  value: {
    fontSize: 16,
    fontWeight: 600,
    color: 'var(--color-deep-navy)',
  },
  divider: {
    height: 1,
    background: 'var(--color-border)',
    margin: '0.25rem 0',
  },
}

function SectionTitle({ icon: Icon, children }) {
  return (
    <div style={styles.sectionTitle}>
      {Icon && <Icon size={14} style={styles.sectionIcon} />}
      {children}
    </div>
  )
}

function StatCard({ label, value }) {
  return (
    <div style={styles.card}>
      <div style={styles.label}>{label}</div>
      <div style={styles.value}>{value  || '—'}</div>
    </div>
  )
}

function Divider() {
  return <div style={styles.divider} />
}

/* ─── main component ─── */

export default function PropertyDetails({ PropertyDetails, isOpen, onClose }) {
  if (!PropertyDetails) return null


  const {
    amenities = [],
    approvals = {},
    highlights = [],
    building_details = {},
    nearby_landmarks = {},
    apartment_size_sqft = {},
    unit_size_ranges = [],
  } = PropertyDetails

  console.log("🚀 ~ PropertyDetails ~ unit_size_ranges:", unit_size_ranges)

  const unitRanges = unit_size_ranges.map((u) => ({
    label: u.unit_type,
    value:
      u.min_size_sqft === u.max_size_sqft
        ? `${u.min_size_sqft} sqft`
        : `${u.min_size_sqft}-${u.max_size_sqft} sqft`,
  }));

  return (
    <Modal
      className="property-details-modal w-90 "
      isOpen={isOpen}
      onClose={onClose}
      title="Property Details"
    >
      <div className="d-flex flex-column gap-4">
        {/* ── Overview stats ── */}
        <div className="overview-grid">
          <StatCard
            label="Size range"
            value={
              apartment_size_sqft?.min && apartment_size_sqft?.max
                ? `${apartment_size_sqft.min.toLocaleString()} – ${apartment_size_sqft.max.toLocaleString()} sq.ft`
                : null
            }
          />

          <StatCard label="Total homes" value={building_details?.total_units} />
          <StatCard label="Floors" value={building_details?.floors} />
          {building_details?.towers && (
            <StatCard label="Towers" value={building_details.towers} />
          )}
        </div>

        <Divider />

        <SectionTitle icon={LayoutTemplate}>Unit Ranges</SectionTitle>

          <div className="overview-grid">
            {unitRanges.map((unit) => (
              <StatCard
                key={unit.label}
                label={unit.label}
                value={unit.value}
              />
            ))}
          </div>

        {/* ── Highlights ── */}
        {highlights.length > 0 && (
          <div>
            <SectionTitle icon={Sparkles}>Highlights</SectionTitle>
            <div className="d-flex flex-wrap gap-1">
              {highlights.map((item, i) => (
               <span key={i} className="highlight-chip">
                  {toTitleCase(item)}
                </span>
              ))}
            </div>
          </div>
        )}

        {highlights.length > 0 && <Divider />}

        {amenities.length > 0 && (
          <PropertyDetailsAccordian
            title={`Amenities (${amenities.length})`}
            defaultOpen={false}
          >
            <div className="d-flex flex-wrap gap-1">
              {amenities.map((amenity, i) => (
                <div
                  key={i}
                  className="amenity-chip d-inline-flex align-center gap-1"
                >
                  <AmenityIcon
                    categories={amenity.categories}
                    name={amenity.name}
                  />

                  {toTitleCase(amenity.name)}

                  {amenity.categories?.[0] && (
                    <span className="amenity-category">
                      · {toTitleCase(amenity.categories[0])}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </PropertyDetailsAccordian>
        )}

        {/* ── Nearby Landmarks ── */}
        <PropertyDetailsAccordian
            title={`Nearby Landmarks (${Object.keys(nearby_landmarks).length})`}
            defaultOpen={false}
          >
            <div className="landmarks-grid">
              {Object.entries(nearby_landmarks).map(([category, values]) =>
                values?.length > 0 ? (
                  <div key={category} style={styles.card}>
                    <div className="landmark-header d-flex align-center gap-1">
                      <LandmarkIcon category={category} />
                      {category.replace(/_/g, ' ')}
                    </div>

                    <div className="d-flex flex-column text-left whitespace-pre-line">
                      {values.map((item, idx) => (
                        <div
                          key={idx}
                          className={`landmark-item ${
                            idx < values.length - 1
                              ? 'landmark-item-bordered'
                              : ''
                          }`}
                        >
                          {item}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null
              )}
            </div>
          </PropertyDetailsAccordian>

        <Divider />

        {/* ── Approvals ── */}
        <div>
          <SectionTitle icon={BadgeCheck}>Approvals</SectionTitle>
          <div className="approvals-grid">
            {[
              { label: 'CMDA approved', key: 'cmda_approved' },
              { label: 'DTCP approved', key: 'dtcp_approved' },
              { label: 'Bank loan', key: 'bank_loan_available' },
            ].map(({ label, key }) => (
              <div
                key={key}
                className="d-flex align-center gap-1"
                style={styles.card}
              >
                <ApprovalIcon value={approvals[key]} />
                <div>
                  <div style={styles.label}>{label}</div>
                  <div
                    style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: 'var(--color-deep-navy)',
                    }}
                  >
                    <ApprovalText value={approvals[key]} />
                  </div>
                </div>
              </div>
            ))}

            {/* RERA spans full width */}
            <div className="approval-rera-card d-flex align-center gap-1">
              <Award size={18} style={{ color: 'var(--color-medium-gray)' }} />
              <div>
                <div style={styles.label}>RERA number</div>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: approvals.rera_registration_number
                      ? 'var(--color-deep-navy)'
                      : 'var(--color-medium-gray)',
                  }}
                >
                  {approvals.rera_registration_number || 'Not available'}
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </Modal>
  )
}
